import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { crmFinishReasonsIds } from '../../helpers/CRMOptionsConfig';
import { selectColor } from '../../core';
import RadioButton from '../RadioButton';
import { getColor } from '../../core/themev3';

const FinishReason = ({
  bulkIds,
  device,
  finishReason,
  handleOnClick,
  buttonDisabled,
  finishReasonSelected,
  setFinishReasonSelected,
  onChangeText,
  publisherType,
  isOptionWithDescription,
  isArchiveAction = false,
  ctaText,
  hasDuplicate,
  isProfessional = false
}) => {
  const isBrasilSite = process.env.REACT_APP_BUILD_SITE.includes('BR');
  const { t } = useTranslation();
  const isPlural = bulkIds && bulkIds.length > 1;
  const [isFromCRM, setIsFromCRM] = useState(false);

  useEffect(() => {
    const hasCookieCRM = Cookies.get('crmSessionId');
    if (hasCookieCRM) setIsFromCRM(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getNumberOfRows = () => {
    if (isOptionWithDescription) return 3;
    if ((isBrasilSite && isPlural) || device === 'mobile') return 2;
    return 1;
  };

  return (
    <FinishReasonContainer device={device} isBrasilSite={isBrasilSite}>
      <Title data-panel='panel-modal-finishReason-title'>
        {t(`panel.avisos.${isArchiveAction ? 'archive' : 'suspend'}Publication.title${isPlural ? '.plural' : ''}`)}
      </Title>
      <Description data-panel='panel-modal-finishReason-description'>
        {t(
          `panel.avisos.${isArchiveAction ? 'archive' : 'suspend'}Publication.bajada${isPlural ? '.plural' : ''}${
            isArchiveAction || isProfessional ? '' : '.particular'
          }`
        )}
      </Description>
      <OptionsContainer>
        {finishReason.map(reason => {
          if (!hasDuplicate && reason.motivo === 'duplicate') return null;
          if (!isFromCRM && crmFinishReasonsIds.includes(reason.motivo)) return null;
          return (
            <RadioButton
              key={reason.id}
              name={`radio-finishReason-${reason.id}`}
              checked={finishReasonSelected.id === reason.id}
              onChange={e => {
                e.stopPropagation();
                setFinishReasonSelected(reason);
              }}
              marginRight='8'
              labelText={t(
                crmFinishReasonsIds.includes(reason.motivo)
                  ? `panel.avisos.suspendPublication.motivo.${reason.motivo}`
                  : `panel.avisos.suspendPublication.motivo.${publisherType}.${reason.motivo}${
                      isPlural ? '.plural' : ''
                    }`
              )}
              dataPanel={`panel-modal-finishReason-radioButton-${reason.id}`}
            />
          );
        })}
        <TextArea
          disabled={!isOptionWithDescription}
          maxLength={80}
          placeholder={t(`panel.avisos.suspendPublication.motivo.other.placeholder${isPlural ? '.plural' : ''}`)}
          rows={getNumberOfRows()}
          onChange={onChangeText}
          data-panel='panel-modal-finishReason-textArea'
        />
      </OptionsContainer>
      {isArchiveAction && (
        <AditionalText data-panel='panel-modal-finishReason-aditionalText'>
          {t(`panel.avisos.archivePublication.aditionalText${isPlural ? '.plural' : ''}`)}
        </AditionalText>
      )}
      <ButtonContainer>
        <CTA disabled={buttonDisabled} onClick={handleOnClick} data-panel='panel-modal-finishReason-buttonCTA'>
          {ctaText || (
            <>
              {t(`panel.avisos.${isArchiveAction ? 'archive' : 'suspend'}Publication.CTA.finish`)}
              {isBrasilSite && isArchiveAction && isPlural ? 's' : ''}
            </>
          )}
        </CTA>
      </ButtonContainer>
    </FinishReasonContainer>
  );
};

const FinishReasonContainer = styled.div`
  padding: ${({ device }) => (device === 'mobile' ? '28px' : '20px 24px')};
  max-width: 507px;
`;

const Title = styled.div`
  font-family: Hind;
  font-size: 20px;
  font-weight: 600;
  line-height: 26px;
  letter-spacing: 0em;
  text-align: center;
  white-space: break-spaces;
  margin-bottom: 22px;
`;

const Description = styled.div`
  margin-bottom: 22px;
  font-family: Hind;
  font-size: 16px;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0em;
  text-align: center;
  white-space: break-spaces;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const TextArea = styled.textarea`
  margin-bottom: ${({ disabled }) => (disabled ? 84 : 40)}px;
  overflow: ${({ disabled }) => (disabled ? 'hidden' : 'auto')};
  resize: none;
  border: 1px solid ${selectColor('grayMediumLight')};
  border-radius: 8px;
  padding: 12px;
  font-family: Hind;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0em;
  text-align: left;
  ::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: ${selectColor('grayMediumDark')};
    opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};
    font-family: Hind;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: 0em;
    text-align: left;
  }

  :-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    color: ${selectColor('grayMediumDark')};
    font-family: Hind;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: 0em;
    text-align: left;
  }

  ::-ms-input-placeholder {
    /* Microsoft Edge */
    color: ${selectColor('grayMediumDark')};
    font-family: Hind;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: 0em;
    text-align: left;
  }
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const CTA = styled.button`
  height: 48px;
  width: 100%;
  padding: 0px 12px;
  border-radius: 8px;
  color: white;
  background-color: ${getColor('primary')};
  font-family: Hind;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 21px;
  letter-spacing: 0em;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  ${({ disabled }) =>
    disabled
      ? css`
          background-color: ${selectColor('grayLight')};
          color: ${selectColor('grayMediumLight')};
          cursor: auto;
        `
      : css`
          :hover {
            background-color: ${getColor('secondary')};
          }
        `};
`;

const AditionalText = styled.div`
  font-family: Hind;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0em;
  text-align: center;
  white-space: normal;
  color: rgb(104, 128, 140);
  margin-bottom: 12px;
`;

export default FinishReason;
